import { CircleInfoIcon } from "@xola/icons";
import { flash, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import dayjs from "dayjs";
import { map } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Channel, ChannelEmailDefaultOffsetMap, ChannelTypeCopy, NotificationPreferenceType, Permission } from "types";
import { ConfirmLeaveModal } from "../../Common/ConfirmLeaveModal";
import { IsGranted } from "../../Common/IsGranted";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { TimePickerPopover } from "../../Common/TimePickerPopover";
import { ChangeEmailButton, EmailType } from "../../Dashboard/ChangeEmailButton";
import { ContentLayout } from "../../Layout/ContentLayout";
import {
    AbandonedBookingReminderChannelsPreference,
    AbandonedBookingReminderPreferenceModal,
} from "./AbandonedBookingReminderPreference";
import {
    NotificationPreferenceListGroup,
    NotificationPreferenceListItem,
    NotificationPreferenceSection,
    useNotificationPreferenceList,
} from "./useNotificationPreferenceList";
import { WaiverReminderChannelsPreference, WaiverReminderPreferenceModal } from "./WaiverReminderPreference";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";

export const NotificationPreferences = () => {
    const { action } = useParams<{ action?: string }>();
    const { isGranted } = useAuthorization();
    const invalidate = useInvalidate();
    const seller = useSeller();

    const hasSMS = isGranted(Permission.SMS_SEND);
    const [formTouched, setFormTouched] = useState(false);
    const gratuityNotificationRef = useRef<HTMLDivElement>(null);

    const preferences = useNotificationPreferenceList(() => {
        flash.success("Successfully saved notification preferences");
    });

    useEffect(() => {
        // Scroll to the gratuity offset setting if redirected from the `Gratuity` page.
        if (gratuityNotificationRef.current && action === "gratuity") {
            gratuityNotificationRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [action]);

    useEffect(() => {
        // SMS toggle has changed so we need to reload the preferences
        preferences.reset();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [hasSMS]);

    const handleSave = async () => {
        preferences.save();
        invalidate("sellers", seller.id);
        setFormTouched(false);
    };

    const handleReset = () => {
        preferences.reset();
        setFormTouched(false);
    };

    return (
        <ContentLayout isWide isDisabled={preferences.isLoading}>
            <h2 className="mt-4">Notifications</h2>
            <div className="divide-y divide-gray-light pb-6">
                <ConfirmLeaveModal isFormTouched={formTouched} />
                {map(
                    preferences.list,
                    (section: NotificationPreferenceSection, key: NotificationPreferenceListGroup) => {
                        return (
                            <div key={key} data-testid={`notification-preference-section-${key}`}>
                                <ContentLayout.Section title={section.title}>
                                    {key === NotificationPreferenceListGroup.OWNER && (
                                        <IsGranted permissions={Permission.ACCOUNT_INFORMATION_VIEW}>
                                            <ChangeEmailButton emailType={EmailType.PRIMARY} />
                                        </IsGranted>
                                    )}

                                    <IsGranted permissions={Permission.ACCOUNT_INFORMATION_VIEW}>
                                        {key === NotificationPreferenceListGroup.COMPANY && (
                                            <ChangeEmailButton emailType={EmailType.COMMUNICATION} />
                                        )}
                                    </IsGranted>

                                    <div className="space-y-6">
                                        {section.preferences.map((item) => {
                                            // Using index of 0 is fine, since both channels will have the same value
                                            const { offset } = item?.channels?.[0] ?? {};
                                            const defaultOffset = ChannelEmailDefaultOffsetMap[item?.type];

                                            // Determine the duration based on the offset or defaultOffset
                                            const duration = offset === "" && defaultOffset ? defaultOffset : offset;

                                            const offsetMinutes = dayjs.duration(duration || "PT0M").asMinutes();

                                            const updatePreferenceChannels = (channels: Channel[]) => {
                                                preferences.update(key, item.type, (item) => {
                                                    item.channels = channels;
                                                });
                                                setFormTouched(true);
                                            };

                                            const updatePreferenceOffset = (offset: number) => {
                                                preferences.update(key, item.type, (item) => {
                                                    for (const channel of item.channels) {
                                                        channel.offset = dayjs.duration(offset, "m").toISOString();
                                                    }
                                                });
                                                setFormTouched(true);
                                            };

                                            const updateAbandonedRecovery = (payload: {
                                                channels: Channel[];
                                                sendStartTime: number;
                                                sendEndTime: number;
                                            }) => {
                                                preferences.update(key, item.type, (draft) => {
                                                    draft.channels = payload.channels;
                                                    draft.sendStartTime = payload.sendStartTime;
                                                    draft.sendEndTime = payload.sendEndTime;
                                                });
                                                setFormTouched(true);
                                            };

                                            const handleChannelToggle = (channelIndex: number, enabled: boolean) => {
                                                preferences.update(key, item.type, (draft) => {
                                                    draft.channels[channelIndex].enabled = enabled;
                                                });
                                                setFormTouched(true);
                                            };

                                            return (
                                                <div
                                                    key={item.type}
                                                    ref={
                                                        item.type ===
                                                        NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER
                                                            ? gratuityNotificationRef
                                                            : null
                                                    }
                                                    className="flex items-start justify-between"
                                                    data-testid="notification-preference-setting"
                                                >
                                                    <div className="space-y-1">
                                                        <div className="text-md font-bold">{item.name}</div>

                                                        <div className="text-gray-darker">
                                                            {item.canOverride ? (
                                                                <PreferenceDetails
                                                                    item={item}
                                                                    offset={offsetMinutes}
                                                                    onUpdateChannel={updatePreferenceChannels}
                                                                    onUpdateOffset={updatePreferenceOffset}
                                                                    onUpdateAbandonedRecovery={updateAbandonedRecovery}
                                                                />
                                                            ) : (
                                                                item.description
                                                            )}
                                                            {item.tooltip && (
                                                                <Tooltip content={item.tooltip} placement="right">
                                                                    <CircleInfoIcon />
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <NotificationChannels
                                                        item={item}
                                                        onApply={updatePreferenceChannels}
                                                        onChannelToggle={handleChannelToggle}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </ContentLayout.Section>
                            </div>
                        );
                    },
                )}
            </div>

            <ContentLayout.Footer preferenceSidebar>
                <SubmitButton isLoading={preferences.isLoading} onClick={handleSave}>
                    Save
                </SubmitButton>

                <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};

interface NotificationChannelsProps {
    item: NotificationPreferenceListItem;
    onApply: (channels: Channel[]) => void;
    onChannelToggle: (channelIndex: number, enabled: boolean) => void;
}

const NotificationChannels = ({ item, onApply, onChannelToggle }: NotificationChannelsProps) => {
    if (item.type === NotificationPreferenceType.WAIVER_REMINDER_TRAVELER) {
        return <WaiverReminderChannelsPreference item={item} onApply={onApply} />;
    }

    if (item.type === NotificationPreferenceType.ABANDONED_RECOVERY_TRAVELER) {
        return <AbandonedBookingReminderChannelsPreference item={item} onApply={onApply} />;
    }

    return <ChannelList channels={item.channels} onChange={onChannelToggle} />;
};

interface ChannelListProps {
    channels: Channel[];
    onChange: (channelIndex: number, enabled: boolean) => void;
}

const ChannelList = ({ channels, onChange }: ChannelListProps) => {
    return (
        <Switch.Group className="h-full flex-col-reverse justify-between">
            {channels.map((channel, channelIndex) => {
                const { enabled, readonly, type } = channel;
                return (
                    <div
                        key={type}
                        className="flex w-full items-center justify-between pt-1"
                        data-testid="notification-preference-button"
                    >
                        <Switch
                            isChecked={enabled}
                            disabled={readonly}
                            size="medium"
                            onChange={(enabled: boolean) => onChange(channelIndex, enabled)}
                        />

                        <Switch.Label direction="left">
                            <span className="pl-2 text-sm text-gray-darker">{ChannelTypeCopy[type]}</span>
                        </Switch.Label>
                    </div>
                );
            })}
        </Switch.Group>
    );
};

interface PreferenceDetailsProps {
    item: NotificationPreferenceListItem;
    offset: number;
    onUpdateChannel: (channels: Channel[]) => void;
    onUpdateOffset: (offset: number) => void;
    onUpdateAbandonedRecovery: (payload: { channels: Channel[]; sendStartTime: number; sendEndTime: number }) => void;
}

const PreferenceDetails = ({
    item,
    offset,
    onUpdateChannel,
    onUpdateOffset,
    onUpdateAbandonedRecovery,
}: PreferenceDetailsProps) => {
    const isAllowedToChangeNegativeOffset = ALLOWED_TYPES_FOR_NEGATIVE_OFFSET.has(item.type);

    if (item.type === NotificationPreferenceType.WAIVER_REMINDER_TRAVELER) {
        return <WaiverReminderPreferenceModal item={item} onApply={onUpdateChannel} />;
    }

    if (item.type === NotificationPreferenceType.ABANDONED_RECOVERY_TRAVELER) {
        return <AbandonedBookingReminderPreferenceModal item={item} onApply={onUpdateAbandonedRecovery} />;
    }

    return (
        <>
            Sent
            <TimePickerPopover
                placement="right"
                value={offset}
                isLoading={false}
                isAllowedToChangeNegativeOffset={isAllowedToChangeNegativeOffset}
                channels={item.channels}
                onApply={onUpdateOffset}
                onApplyNegativeOffset={onUpdateChannel}
            />{" "}
            {item.overriddenDescription}
        </>
    );
};

const ALLOWED_TYPES_FOR_NEGATIVE_OFFSET = new Set([NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER]);
